import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Check } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { canBuyLeads } from '@/lib/roles';
import { selfResendStatus, selfResendOnboarding } from '@/lib/customerSupport';

/**
 * "Resend setup email" — for customers who HAVE credits but have NEVER unlocked
 * a lead (the classic "I paid, now what?" support ticket). Eligibility is
 * decided server-side by onboarding-resend; the link renders only when the
 * server says eligible, and each account may use it once.
 */
const ResendSetupEmail: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const { profile } = useAuth();
  const [eligible, setEligible] = useState(false);
  const [balance, setBalance] = useState(0);
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'error'>('idle');
  const [note, setNote] = useState('');

  const buyer = !!profile && canBuyLeads(profile.user_type);

  useEffect(() => {
    if (!buyer) return;
    let alive = true;
    void selfResendStatus().then((r) => {
      if (!alive || !r.ok || !r.data) return;
      setEligible(!!r.data.eligible);
      setBalance(Number(r.data.balance || 0));
    });
    return () => {
      alive = false;
    };
  }, [buyer, profile?.id]);

  if (!buyer || (!eligible && state !== 'sent')) return null;

  const send = async () => {
    setState('busy');
    const r = await selfResendOnboarding();
    if (r.ok && r.data?.sent) {
      setState('sent');
      setNote(r.data.to || profile?.email || '');
    } else {
      setState('error');
      setNote(
        r.data?.skipped === 'already_resent'
          ? es ? 'Ya le reenviamos la guía. Revise su bandeja de entrada y spam.' : 'We already resent the guide. Check your inbox and spam folder.'
          : es ? 'No pudimos enviarlo ahora. Escriba a support@giggone.com.' : 'We could not send it right now. Email support@giggone.com.'
      );
    }
  };

  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
        <div>
          <p className="text-sm font-bold text-slate-900">
            {es
              ? `Tiene ${balance} ${balance === 1 ? 'crédito listo' : 'créditos listos'} y aún no ha desbloqueado un lead.`
              : `You have ${balance} ${balance === 1 ? 'credit' : 'credits'} ready and haven't unlocked a lead yet.`}
          </p>
          <p className="text-xs text-slate-600">
            {state === 'sent'
              ? es ? `Guía enviada a ${note}. Revise también la carpeta de spam.` : `Setup guide sent to ${note}. Check your spam folder too.`
              : state === 'error'
              ? note
              : es
              ? 'Le enviamos de nuevo la guía paso a paso: elegir proyecto, desbloquear y contactar al cliente.'
              : 'We can email you the step-by-step setup guide again: pick a project, unlock it, contact the homeowner.'}
          </p>
        </div>
      </div>
      {state === 'sent' ? (
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700">
          <Check className="h-4 w-4" /> {es ? 'Enviado' : 'Sent'}
        </span>
      ) : state !== 'error' ? (
        <button
          type="button"
          onClick={send}
          disabled={state === 'busy'}
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-orange-300 bg-white px-4 py-2 text-sm font-bold text-orange-700 underline-offset-2 hover:underline disabled:opacity-50"
        >
          {state === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          {es ? 'Reenviar correo de configuración' : 'Resend setup email'}
        </button>
      ) : null}
    </div>
  );
};

export default ResendSetupEmail;
